import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { SegmentsApi, type SegmentRef } from '@/services/targeting/targetingLookupApi'
import type { ObjectRef, Targeting } from '@/services/targeting/targeting'

/** The ids of every segment a targeting object names, included and excluded. */
export function segmentIdsIn(targeting: Targeting): string[] {
  return (targeting.userSegmentTargets ?? []).flatMap((row) => [
    ...(row.userSegments ?? []).map((ref) => ref.id),
    ...(row.notUserSegments ?? []).map((ref) => ref.id),
  ])
}

/**
 * Segment targeting comes back from the API as bare ids whenever the server
 * cannot find the segment behind one (`UserSegmentTargetSerialiser` writes the
 * name only when its lookup succeeds), so a saved line item can show a row of
 * ids where it should show names.
 *
 * The segments are therefore asked for by id — a network can hold far more of
 * them than one page of the list, so the ones a line item targets cannot be
 * looked for in that page.
 */
export function useSegmentNames(ids: string[]) {
  // Sorted and de-duplicated, so the same set of segments is one cache entry
  // however the targeting happens to order them.
  const wanted = useMemo(() => [...new Set(ids.filter(Boolean))].sort(), [ids])

  const { data: segments = [] } = useQuery({
    queryKey: ['targeting-segments-by-id', wanted],
    queryFn: () => SegmentsApi.getByIds(wanted),
    enabled: wanted.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  return useMemo(() => {
    const known = new Map(segments.map((segment) => [segment.id, segment]))
    // What the lookup knows fills the gaps in a ref, which leaves the id
    // showing only for a segment the network cannot see at all.
    const fill = (refs: ObjectRef[] | undefined): SegmentRef[] =>
      (refs ?? []).map((ref) => ({
        ...known.get(ref.id),
        ...ref,
        name: ref.name || known.get(ref.id)?.name,
      }))

    return {
      /** The same refs, each with its name filled in where one is known. */
      fill,
      /** The same targeting, with its segment refs named. */
      inTargeting: (targeting: Targeting): Targeting =>
        targeting.userSegmentTargets?.length
          ? {
              ...targeting,
              userSegmentTargets: targeting.userSegmentTargets.map((row) => ({
                ...row,
                userSegments: fill(row.userSegments),
                notUserSegments: fill(row.notUserSegments),
              })),
            }
          : targeting,
    }
  }, [segments])
}
